'use client'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useParams, useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

import {
  AlertBox,
  AlertBoxType,
  Button,
  Heading,
  Icon,
  IconComponent,
  Input,
  InputHelperText,
  InputLabel,
  Paragraph,
  TextArea,
} from '@graftcode/design-system'
import { zodResolver } from '@hookform/resolvers/zod'

import { AppRoute } from '@common/core/enums'
import { useToastStore } from '@common/core/stores'
import { PROJECT_CREATED_SUCCESS_TOAST_DISMISS_TEST_ID } from '@common/core/stores/toast/const/project-created-success-toast-dismiss-test-id.const'
import { PROJECT_CREATED_SUCCESS_TOAST_TEST_ID } from '@common/core/stores/toast/const/project-created-success-toast-test-id.const'
import { addProject, getProjectsByUserId } from '@common/modules/projects/stores/projects.actions'
import { switchCurrentProject } from '@common/modules/projects/utils/switchCurrentProject'
import { useWorkspacesStore } from '@common/modules/workspaces'
import { updateLastUsedProject } from '@common/modules/workspaces/stores/workspaces.actions'

import { useProjectsStore } from '../stores'

import { type CreateProjectFormData, createProjectSchema } from './Create.schema'
import type { Props } from './Create.types'

import styles from './Create.module.scss'

export function CreateProjectForm({ onSuccess, onCancel, onSubmittingChange }: Props) {
  // Hooks
  const t = useTranslations('projects')
  const router = useRouter()
  const { locale } = useParams()
  const showToast = useToastStore((s) => s.showToast)
  const currentWorkspace = useWorkspacesStore((s) => s.currentWorkspace)
  const projects = useProjectsStore((s) => s.projects)

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
    setError,
  } = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectSchema((key) => t(`create.${key}`))),
    defaultValues: {
      name: '',
      description: '',
    },
  })

  useEffect(() => {
    onSubmittingChange?.(isSubmitting)
  }, [isSubmitting, onSubmittingChange])

  // Handlers
  const handleCancel = () => {
    onCancel?.()
  }

  // Methods
  const onSubmit = async (data: CreateProjectFormData) => {
    const workspaceId = currentWorkspace?.id
    if (!workspaceId) {
      setError('root', {
        type: 'manual',
        message: t('create.errors.noWorkspace'),
      })
      return
    }

    const isFirstProject = projects.length === 0

    try {
      const projectId = await addProject({
        name: data.name,
        description: data.description,
        workspaceId,
      })

      if (!projectId) {
        setError('root', {
          type: 'manual',
          message: t('create.errors.createFailed'),
        })
        showToast({
          variant: 'error',
          title: t('create.errors.alertTitle'),
          description: t('create.errors.createFailed'),
        })
        return
      }

      switchCurrentProject({
        id: projectId,
        name: data.name,
        description: data.description,
        workspaceId,
      })
      void updateLastUsedProject(workspaceId, projectId)

      await getProjectsByUserId(workspaceId)

      const newProject = useProjectsStore.getState().projects.find((p) => p.id === projectId)
      if (newProject) {
        switchCurrentProject(newProject)
      }

      showToast({
        variant: 'success',
        title: t('create.toast.successTitle', { name: data.name }),
        description: t('create.toast.successDescription'),
        testId: PROJECT_CREATED_SUCCESS_TOAST_TEST_ID,
        primaryAction: {
          label: t('create.toast.openSettings'),
          variant: 'primary',
          onClick: () => router.push(`/${locale}/${AppRoute.SETTINGS_PROJECT}`),
        },
        secondaryAction: {
          label: t('create.toast.close'),
          variant: 'ghost',
          testId: PROJECT_CREATED_SUCCESS_TOAST_DISMISS_TEST_ID,
        },
      })

      onSuccess?.(isFirstProject)
    } catch {
      setError('root', {
        type: 'manual',
        message: t('create.errors.unexpectedError'),
      })
      showToast({
        variant: 'error',
        title: t('create.errors.alertTitle'),
        description: t('create.errors.unexpectedError'),
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.formContent}>
        <div className={styles.formHeader}>
          <div className={styles.iconWrapper}>
            <IconComponent icon={Icon.FolderRegular} size={32} />
          </div>
          <div className={styles.heading}>
            <div id="project-dialog-title" className={styles.headingTitle}>
              <Heading type="h2">{t('create.title')}</Heading>
            </div>
            <div id="project-dialog-description" className={styles.headingDescription}>
              <Paragraph>{t('create.description')}</Paragraph>
            </div>
          </div>
        </div>

        <div className={styles.formFields}>
          <div className={styles.formField}>
            <InputLabel htmlFor="project-name">{t('create.nameLabel')}</InputLabel>
            <Input
              id="project-name"
              size="md"
              variant={errors.name ? 'error' : 'default'}
              placeholder={t('create.namePlaceholder')}
              disabled={isSubmitting}
              {...register('name')}
            />
            {errors.name?.message && <InputHelperText variant="error">{errors.name.message}</InputHelperText>}
          </div>

          <div className={styles.formField}>
            <InputLabel htmlFor="project-description">{t('create.descriptionLabel')}</InputLabel>
            <TextArea
              id="project-description"
              size="md"
              variant={errors.description ? 'error' : 'default'}
              placeholder={t('create.descriptionPlaceholder')}
              disabled={isSubmitting}
              rows={5}
              {...register('description')}
            />
          </div>
        </div>

        <div className={styles.formError}>
          {errors.root?.message && (
            <AlertBox
              size="sm"
              className={styles.alertBox}
              title={t('errors.alertTitle')}
              description={errors.root.message}
              type={AlertBoxType.Critical}
              showCloseButton={false}
            />
          )}
        </div>
      </div>

      <div className={styles.formActions}>
        <Button type="button" variant="outlined" size="l" onClick={handleCancel} disabled={isSubmitting}>
          {t('create.cancelButton')}
        </Button>
        <Button type="submit" variant="primary" size="l" disabled={(!isDirty && !isValid) || isSubmitting}>
          {isSubmitting ? t('create.submitting') : t('create.submitButton')}
        </Button>
      </div>
    </form>
  )
}
